import _ from 'lodash';
import moment from 'moment';
import Vue from 'vue';
import {AxiosResponse} from 'axios';
import {mapActions, mapGetters, mapState} from 'vuex';
import {AlertNotificationModuleConstant, EmployeeProfileModuleConstant, FreeProgramConstant, PageDriverConstant, RegularExpressionConstant, SectionConstant, StorageConstant, UrlModuleConstant} from '../../lib/constants';
import {coachService} from '../../lib/services/coachService';
import {personService} from '../../lib/services/personService';
import StorageService from '../../lib/services/storageService';
import TrainerizeUrlService from '../../lib/services/trainerizeUrlService';
import PtTaskListPartial from './ptTaskList.html';
import MobileUtils from '../../lib/helpers/mobileUtils';
import EcCommon from '../../../types/ec/ecCommon';
import LtCommon from '../../../types/lt/ltCommon';
import LtFramework from '../../../types/lt/ltFramework';
import PtClientList from '../../../types/ec/ecPtClientList';

interface IComponentData {
	coachService: EcCommon.ICoachService;
	personService: EcCommon.IPersonService;
	initializing: boolean;
	mobileUtils: LtCommon.IMobileUtils;
	showFreeProgram: boolean;
	coachTaskList: PtClientList.ITaskList[];
	trainerizeToken: EcCommon.ITrainerizeToken;
}

export default Vue.component('ecPtTaskList', {
	template: PtTaskListPartial,
	data(): IComponentData {
		return {
			personService,
			coachService,
			initializing: true,
			mobileUtils: {} as LtCommon.IMobileUtils,
			showFreeProgram: false,
			coachTaskList: [],
			trainerizeToken: {} as EcCommon.ITrainerizeToken,
		};
	},
	computed: {
		errorMessage(): string {
			let errorMessage = '';
			if (this.coachService.errorMessage) {
				errorMessage = this.coachService.errorMessage;
			}

			if (this.personService.errorMessage) {
				errorMessage = this.personService.errorMessage;
			}

			return errorMessage;
		},
		loading(): boolean {
			return this.initializing || this.coachService.loading || this.personService.loading;
		},
		...mapGetters(EmployeeProfileModuleConstant.name, {
			getIsEmployeeProfileValid: EmployeeProfileModuleConstant.getter.getIsValid,
		}),
		...mapGetters(UrlModuleConstant.name, [UrlModuleConstant.getter.getGlobalPageDriverUrl]),
		...mapState(EmployeeProfileModuleConstant.name, [EmployeeProfileModuleConstant.state.employeeProfile]),
	},
	methods: {
		goToFreeProgramsMaintenance(): void {
			window.location.assign(this.getGlobalPageDriverUrl(SectionConstant.coaching, PageDriverConstant.freeProgram));
		},
		getTrainerizeToken(suppressError: boolean): Promise<void> {
			return this.coachService.getSSOForTrainerize(this.employeeProfile.partyId, suppressError)
				.then((value: AxiosResponse<EcCommon.ITrainerizeToken>) => {
					this.trainerizeToken = value.data;
				});
		},
		goToVLT(): void {
			if (this.mobileUtils.isMobile) {
				const appWindow = window.open('trainerize://', '_self');
				setTimeout(() => {
					if (appWindow) {
						window.location.assign(this.mobileUtils.storeUrl);
					}
				}, 1000);
			} else {
				this.trainerizeToken = {} as EcCommon.ITrainerizeToken;
				const newWindow = window.open('', '_blank');
				this.getTrainerizeToken(false)
					.then(() => {
						const params: LtCommon.IParamsObject = {
							returnUrl: 'overview.aspx',
							loginToken: this.trainerizeToken.token,
							userId: this.trainerizeToken.sourceId,
						};

						const trainerizeUrl = TrainerizeUrlService.getTrainerizeUrlLogin(params);
						if (newWindow) {
							newWindow.location.assign(trainerizeUrl);
						}
					});
			}
		},
		showFreeProgramMaintenance(): Promise<void> {
			return this.personService.getPersonRoles(FreeProgramConstant.ApplicationId, FreeProgramConstant.RoleId)
				.then((response: AxiosResponse<EcCommon.IEmployeeUser[]>) => {
					const employee = _.find(response.data, ['EmployeeId', (this.employeeProfile.employeeDetails as LtFramework.IEmployeeDetails).employeeId]);
					if (employee) {
						const role =  _.find(employee.Roles, (employeeRole: EcCommon.IRole) => {
							return employeeRole.ApplicationId.toUpperCase() === FreeProgramConstant.ApplicationId && employeeRole.RoleId.toUpperCase() === FreeProgramConstant.RoleId;
						});
						if (role) {
							this.showFreeProgram = true;
						}
					}
				});
		},
		goToClientsInTrainerize(tag: string): void {
			if (this.mobileUtils.isMobile) {
				const appWindow = window.open('trainerize://', '_self');
				setTimeout(() => {
					if (appWindow) {
						window.location.assign(this.mobileUtils.storeUrl);
					}
				}, 1000);
			} else {
				this.trainerizeToken = {} as EcCommon.ITrainerizeToken;
				const newWindow = window.open('', '_blank');
				this.getTrainerizeToken(false)
					.then(() => {
						const params: LtCommon.IParamsObject = {
							returnUrl: '/app/group/client.aspx?systemtag=' + tag,
							loginToken: this.trainerizeToken.token,
							userID: this.trainerizeToken.sourceId,
						};

						const trainerizeUrl: string = TrainerizeUrlService.getTrainerizeUrlLogin(params);
						if (newWindow) {
							newWindow.location.assign(trainerizeUrl);
						}
					});
			}
		},
		getTaskList(): Promise<void> {
			return this.coachService.getTaskList(this.employeeProfile.partyId)
				.then((response: AxiosResponse<PtClientList.ITaskList[]>) => {
					_.forEach(response.data, (t) => {
						t.partyId = this.employeeProfile.partyId;
					});

					let coachTaskList: PtClientList.ITaskList[] = [];
					const storedTasks = StorageService.get<PtClientList.IDismissedTaskList>(StorageConstant.dismissedTaskList) as PtClientList.IDismissedTaskList;
					if (storedTasks && storedTasks.expiration && moment().isBefore(moment(storedTasks.expiration))) {
						const dismissedTasks: PtClientList.ITaskList[] = storedTasks && storedTasks.data ? storedTasks.data : [];

						_.forEach(response.data, (t) => {
							const dismissedTask = _.find(dismissedTasks, (dt) => {
								return dt.code === t.code && dt.partyId === this.employeeProfile.partyId;
							});
							if (!dismissedTask) {
								coachTaskList.push(t);
							}
						});
					} else {
						StorageService.remove(StorageConstant.dismissedTaskList);
						coachTaskList = response.data;
					}

					this.coachTaskList = _.orderBy(coachTaskList, ['priority']);
				});
		},
		dismissTask(task: PtClientList.ITaskList): void {
			let dismissedTasks: PtClientList.ITaskList[] = [];
			const storedTasks = StorageService.get<PtClientList.IDismissedTaskList>(StorageConstant.dismissedTaskList) as PtClientList.IDismissedTaskList;
			if (storedTasks && storedTasks.data) {
				dismissedTasks = storedTasks.data;
			}

			const coachTaskIndex = _.findIndex(this.coachTaskList, (t) => {
				return t.code === task.code;
			});
			if (coachTaskIndex !== -1) {
				this.coachTaskList.splice(coachTaskIndex, 1);
				dismissedTasks.push(task);
			}

			const tasks: PtClientList.IDismissedTaskList = {
				data: dismissedTasks,
				expiration: moment().endOf('day').format(),
			};

			StorageService.set<PtClientList.IDismissedTaskList>(StorageConstant.dismissedTaskList, tasks);
		},
		...mapActions(AlertNotificationModuleConstant.name, {
			synchronizeAlertNotificationStore: AlertNotificationModuleConstant.action.synchronize,
		}),
		...mapActions(EmployeeProfileModuleConstant.name, {
			synchronizeEmployeeProfileStore: EmployeeProfileModuleConstant.action.synchronize,
		}),
		...mapActions(UrlModuleConstant.name, {
			synchronizeUrlStore: UrlModuleConstant.action.synchronize,
		}),
		...mapActions(AlertNotificationModuleConstant.name, [AlertNotificationModuleConstant.action.open]),
	},
	created(): void {
		this.mobileUtils = MobileUtils();

		if (window.__PRERENDER_INJECTED || RegularExpressionConstant.authorRegex.test(window.location.href)) {
			return;
		}

		Promise.all([this.synchronizeAlertNotificationStore(), this.synchronizeEmployeeProfileStore(), this.synchronizeUrlStore()]).then(() => {
			if (!this.getIsEmployeeProfileValid) {
				window.location.assign(this.getGlobalPageDriverUrl(SectionConstant.global, PageDriverConstant.loginResource));
			} else {
				this.getTrainerizeToken(true);
				this.getTaskList();
				this.showFreeProgramMaintenance();
			}
		}).finally(() => {
			this.initializing = false;
		});
	},
});
